import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["menu", "trigger"]
  static values = { 
    open: Boolean,
    closeOnOutsideClick: { type: Boolean, default: true },
    useFixed: { type: Boolean, default: false }
  }

  // Global registry to track all dropdown instances
  static instances = new Set()

  connect() {
    // Register this dropdown instance
    this.constructor.instances.add(this)
    
    // Bind methods to maintain context
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleEscapeKey = this.handleEscapeKey.bind(this)
    this.handleScroll = this.repositionIfOpen.bind(this)
    this.handleResize = this.repositionIfOpen.bind(this)
  }

  disconnect() {
    // Unregister this dropdown instance
    this.constructor.instances.delete(this)
    this.removeGlobalListeners()
    
    // Clean up scroll/resize listeners
    window.removeEventListener('scroll', this.handleScroll, { capture: true })
    window.removeEventListener('resize', this.handleResize)
    
    // Reset positioning
    if (this.hasMenuTarget) {
      this.menuTarget.style.position = ''
      this.menuTarget.style.top = ''
      this.menuTarget.style.right = ''
      this.menuTarget.style.left = ''
      this.menuTarget.style.zIndex = ''
    }
  }

  toggle(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    // Close all other dropdowns first
    this.closeAllOthers()
    
    this.openValue = true
    this.menuTarget.classList.remove('hidden')
    this.triggerTarget?.setAttribute('aria-expanded', 'true')
    
    // Position the menu if using fixed positioning
    if (this.useFixedValue && this.hasTriggerTarget && this.hasMenuTarget) {
      this.positionFixedMenu()
      
      // Add scroll/resize listeners for repositioning
      window.addEventListener('scroll', this.handleScroll, { capture: true })
      window.addEventListener('resize', this.handleResize)
    }
    
    // Add global listeners
    this.addGlobalListeners()
    
    // Dispatch custom event
    this.dispatch('opened', { detail: { dropdown: this } })
  }

  close() {
    this.openValue = false
    this.menuTarget.classList.add('hidden')
    this.triggerTarget?.setAttribute('aria-expanded', 'false')
    
    // Reset positioning when closed
    if (this.useFixedValue && this.hasMenuTarget) {
      this.menuTarget.style.position = ''
      this.menuTarget.style.top = ''
      this.menuTarget.style.right = ''
      this.menuTarget.style.left = ''
      this.menuTarget.style.zIndex = ''
    }
    
    // Remove global listeners
    this.removeGlobalListeners()
    
    // Remove scroll/resize listeners
    window.removeEventListener('scroll', this.handleScroll, { capture: true })
    window.removeEventListener('resize', this.handleResize)
    
    // Dispatch custom event
    this.dispatch('closed', { detail: { dropdown: this } })
  }

  positionFixedMenu() {
    const trigger = this.triggerTarget
    const menu = this.menuTarget
    const rect = trigger.getBoundingClientRect()
    
    // Use fixed positioning to break out of overflow containers
    menu.style.position = "fixed"
    menu.style.top = `${rect.bottom + 4}px` // 4px gap below button
    menu.style.zIndex = "9999"
    
    // Align to the right edge of the trigger button
    menu.style.right = `${window.innerWidth - rect.right}px`
    menu.style.left = "auto"
    
    // Check if menu goes below viewport
    const menuHeight = menu.offsetHeight || 200
    if ((rect.bottom + 4 + menuHeight) > window.innerHeight) {
      // Show above the button instead
      menu.style.top = `${rect.top - menu.offsetHeight - 4}px`
    }
    
    // Check if menu goes beyond right edge of viewport
    const menuWidth = menu.offsetWidth || 192 // w-48 = 192px
    if ((rect.right - menuWidth) < 0) {
      menu.style.right = "auto"
      menu.style.left = `${rect.left}px`
    }
    
    // Check if menu goes beyond left edge of viewport
    if (rect.left < 0) {
      menu.style.left = "4px"
      menu.style.right = "auto"
    }
  }

  repositionIfOpen() {
    if (this.openValue && this.useFixedValue) {
      this.positionFixedMenu()
    }
  }

  closeAllOthers() {
    this.constructor.instances.forEach(instance => {
      if (instance !== this && instance.openValue) {
        instance.close()
      }
    })
  }

  closeAll() {
    this.constructor.instances.forEach(instance => {
      if (instance.openValue) {
        instance.close()
      }
    })
  }

  handleOutsideClick(event) {
    if (!this.closeOnOutsideClickValue) return
    
    // Don't close if clicking inside the dropdown
    if (this.element.contains(event.target)) return
    
    this.close()
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      this.close()
      this.triggerTarget?.focus() // Return focus to trigger
    }
  }

  addGlobalListeners() {
    if (this.closeOnOutsideClickValue) {
      document.addEventListener('click', this.handleOutsideClick, { capture: true })
    }
    document.addEventListener('keydown', this.handleEscapeKey)
  }

  removeGlobalListeners() {
    document.removeEventListener('click', this.handleOutsideClick, { capture: true })
    document.removeEventListener('keydown', this.handleEscapeKey)
  }

  // Convenience method to close dropdown when clicking menu items
  closeOnItemClick(event) {
    // Only close if it's a link or button, not a form submission
    if (event.target.tagName === 'A' || event.target.closest('a')) {
      this.close()
    }
  }

  // Method to programmatically close all dropdowns (useful for navigation)
  static closeAll() {
    this.instances.forEach(instance => {
      if (instance.openValue) {
        instance.close()
      }
    })
  }
}